import { useCallback, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";

import { formatTimeOfDay } from "../shifts/shiftHelpers";

import {
  endRunnerShiftMutation,
  myRunnerShiftStatusQuery,
  startRunnerShiftMutation,
} from "./queries";
import {REFRESH_INTERVAL_MS} from "../constants";

export default function useRunnerShiftStatus({ enabled = true } = {}) {
  const [error, setError] = useState(null);

  const { data, loading, refetch } = useQuery(myRunnerShiftStatusQuery, {
    skip: !enabled,
    fetchPolicy: "network-only",
    pollInterval: REFRESH_INTERVAL_MS,
    onError: () =>
      setError("Could not check whether you're on shift. Try the switch again."),
  });

  const [runStart, { loading: starting }] = useMutation(
    startRunnerShiftMutation,
  );
  const [runEnd, { loading: ending }] = useMutation(endRunnerShiftMutation);

  const status = data?.myRunnerShiftStatus ?? null;
  const onShift = status?.status === "ACTIVE";
  const shift = status?.shift ?? null;

  const toggle = useCallback(async () => {
    setError(null);

    try {
      if (onShift) {
        await runEnd();
      } else {
        await runStart();
      }

      await refetch();
    } catch (err) {
      setError(
        onShift
          ? "Could not take you off shift. Try again."
          : "Could not put you on shift. Try again.",
      );
    }
  }, [onShift, runEnd, runStart, refetch]);

  return {
    onShift,
    /* Both null on an ad-hoc stint: the runner is on, but outside every
     * configured window, so there is no name or posted hours to show. */
    shiftName: shift?.name ?? null,
    shiftWindow: shift
      ? `${formatTimeOfDay(shift.startTime)} - ${formatTimeOfDay(shift.endTime)}`
      : null,
    startedAt: status?.startedAt ?? null,
    busy: loading || starting || ending,
    error,
    clearError: () => setError(null),
    toggle,
  };
}
